import React, {useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {GUEST_APP_EVENTS_NAME, RESERVATION_PAYMENT_TYPES} from 'utils/constants';
import {setReservationId} from '../store/slices/guestParamsEventsSlice';
import {setType} from 'store/slices/paymenEventParamsSlice';
import {RootState} from 'store/rootReducer';
import {mixpanelTrack} from '../analytics/mixpanel';
import {useDevice} from '../context/device';
import {useStoredURLParams} from '../context/storedURLParams';

const useMixPanel = () => {
  const {device} = useDevice();
  const {reservationId} = useStoredURLParams();

  const [detectedDevice, setDetectedDevice] = React.useState<string | null>(null);
  const [detectedReservationId, setDetectedReservationId] = React.useState<string>('');
  const dispatch = useDispatch();
  const eventParams = useSelector((state: RootState) => state.guestAppEventParamsSlice);

  const paymentParams = useSelector((state: RootState) => state.paymenEventParamsSlice);

  const idVerificationsParams = useSelector(
    (state: RootState) => state.identityVerificationParamSlice,
  );

  type IDVerificationParams = {
    type?: string;
    status: string;
    picture?: string;
    distance?: string;
  };

  const IdVerificationStart = () => {
    mixpanelTrack('IDVerf - starts', {
      'Reservation ID': detectedReservationId,
      Device: detectedDevice as string,
    });
  };

  const IdSelfieVerification = (name: string, props: IDVerificationParams) => {
    const params = {
      ...{
        'Reservation ID': detectedReservationId,
        Device: detectedDevice as string,
      },
      ...props,
    };
    mixpanelTrack(name, params);
  };

  const IdVerificationDocument = useCallback(
    (status: string) => {
      let params = {
        Type: idVerificationsParams.type,
        Picture: idVerificationsParams.picture,
        Status: status,
        'Reservation ID': detectedReservationId,
      };

      mixpanelTrack(idVerificationsParams.eventName as string, params as any);
    },
    [idVerificationsParams, detectedReservationId],
  );

  const IdGuestAppEvents = React.useCallback(
    (name: string, props?: any) => {
      let params: {[key: string]: any} = {};
      params['Reservation ID'] = props?.reservationId || eventParams.reservationId;
      params['Is User'] = eventParams.isUser;
      let firstTime = localStorage.getItem('upselling_first_time');

      if (name === GUEST_APP_EVENTS_NAME.visited && params['Reservation ID']) {
        dispatch(setReservationId(params['Reservation ID']));
      }

      if (name === GUEST_APP_EVENTS_NAME.upsellings) {
        params['Housing ID'] = eventParams.housingId;

        if (!firstTime) {
          params['First Time'] = true;
          localStorage.setItem('upselling_first_time', 'true');
        }

        if (firstTime === 'true') {
          localStorage.setItem('upselling_first_time', 'false');
          params['First Time'] = false;
        }
      }

      if (
        name === GUEST_APP_EVENTS_NAME.dealClicked ||
        name === GUEST_APP_EVENTS_NAME.dealRequested
      ) {
        params['Housing ID'] = eventParams.housingId;
        params['Deal Name'] = props?.dealName || eventParams.dealName;
        params['First Time'] = firstTime === 'true';
      }

      if (
        name === GUEST_APP_EVENTS_NAME.experiencieShowModal ||
        name === GUEST_APP_EVENTS_NAME.experiencieIgnoredModal
      ) {
        params['Housing ID'] = eventParams.housingId;
      }
      mixpanelTrack(name, params);
    },
    [
      eventParams.reservationId,
      dispatch,
      eventParams.dealName,
      eventParams.housingId,
      eventParams.isUser,
    ],
  );

  const PaymentEvents = useCallback(
    (name: string, props?: any) => {
      let defaultParams: {[key: string]: any} = {};
      defaultParams['Reservation ID'] = eventParams.reservationId;
      let params = {...defaultParams, ...props};

      if (
        name === GUEST_APP_EVENTS_NAME.paymentRequest &&
        props.type !== RESERVATION_PAYMENT_TYPES.segurityDeposit
      ) {
        dispatch(setType({type: props.type, paymentId: props.paymentId}));
      }

      mixpanelTrack(name, params);
    },
    [eventParams.reservationId, dispatch],
  );

  const DispatchGeneralPayments = React.useCallback(
    (status: boolean) => {
      paymentParams.forEach(pp =>
        PaymentEvents(GUEST_APP_EVENTS_NAME.paymentCompleted, {
          ...pp,
          ...{status: status},
        }),
      );
    },
    [paymentParams, PaymentEvents],
  );

  React.useEffect(() => {
    setDetectedDevice(device);
    setDetectedReservationId(reservationId);
  }, [device, reservationId]);

  return {
    IdVerificationStart,
    IdVerificationDocument,
    IdGuestAppEvents,
    PaymentEvents,
    DispatchGeneralPayments,
    IdSelfieVerification,
  };
};

export default useMixPanel;
